import React, { useContext } from 'react';
import { Badge, Label } from '@nooz-optics/nooz-components-lib';
import {
  formatContentfulComponentProps,
  returnCurrencyCharFromCode,
} from '../../../utils/globals';
import { CheckoutContext } from '../../../utils/providers/checkout';
import { ProductPrice } from '../../UI/Elements/Price/productPrice';
import { List } from '../../UI/Navigation/List/list';
import { LineOrderItem } from '../LineOrderItem/lineOrderItem';
import { EmptyCart } from './Empty/emptyCart';
import { returnPackPrice } from './cart.utils';

const LoadingCartItem = () => (
  <div className='animate-pulse flex space-x-4'>
    <div className='rounded-md bg-gray-300 h-24 w-24' />
    <div className='flex-1 space-y-4 py-1'>
      <div className='h-4 bg-gray-300 rounded w-3/4' />
      <div className='h-4 bg-gray-300 rounded w-2/4' />
      <div className='h-4 bg-gray-300 rounded w-1/4' />
    </div>
  </div>
);

export const Cart = ({ body }) => {
  const {
    checkout,
    loading,
    handleLineItemRemove,
    handleLineItemQuantityChange,
  } = useContext(CheckoutContext);
  const isEmptyCart = checkout[0].lineItems?.length === 0;
  const currency = returnCurrencyCharFromCode(checkout[0].currencyCode);

  let packLineOrderList = [];
  let lineOderItemList = [];

  // eslint-disable-next-line no-unused-expressions
  checkout[0].lineItems?.forEach((item) => {
    // get line item pack attributes
    const packAttr = item.customAttributes.find((attr) => attr.key === 'pack');

    // has a pack attribute
    if (packAttr) {
      // get pack id
      const existingPack = packLineOrderList.find(
        (pack) => pack.id === packAttr.value
      );

      // pack id not found
      if (!existingPack) {
        // create new pack list
        packLineOrderList.push({
          id: packAttr.value,
          items: [item],
        });
      } else {
        // pack id found, add item to pack list
        existingPack.items.push(item);
      }
    } else {
      // no pack attribute
      lineOderItemList.push(item);
    }
  });

  lineOderItemList = lineOderItemList.map((item) => {
    const handleLineItemRemoveOnClick = () => handleLineItemRemove([item]);
    return {
      spacing: 'py-2',
      children: (
        <LineOrderItem
          compareAtPrice={item.variant?.compareAtPrice?.amount}
          handleLineItemQuantityChange={handleLineItemQuantityChange}
          handleLineItemRemove={handleLineItemRemoveOnClick}
          image={item.variant?.image}
          item={item}
          price={item.variant?.price}
          title={item.title}
          removeLabel={body.removeLabel}
        />
      ),
    };
  });

  packLineOrderList = packLineOrderList.map((pack) => {
    const {
      roundedTotalPricePackItemsWithDiscount,
      roundedTotalPricePackItemsNoDiscount,
    } = returnPackPrice(pack);

    const packItems = pack.items;
    const packLabel = pack.id;
    const handleLineItemRemoveOnClick = () => handleLineItemRemove(packItems);

    return {
      spacing: 'py-2',
      children: (
        <div className=''>
          <div className='flex items-start justify-between'>
            <div className='flex flex-col items-start'>
              <div className='flex items-center'>
                <Label
                  size='2xl'
                  transform='capitalize'
                  weight='medium'
                  text={packLabel}
                />
                <div className='ml-2'>
                  <Badge
                    type='contained'
                    label={{ text: 'Pack' }}
                    color={{ classname: 'primary' }}
                    dotColor={
                      packItems &&
                      packItems[0].categories &&
                      packItems[0].categories[0].color
                    }
                    size='sm'
                  />
                </div>
              </div>
              <div className='flex flex-col w-full'>
                {packItems?.map((item) => (
                  <div className='flex-1'>
                    <LineOrderItem
                      image={item.variant?.image}
                      item={item}
                      title={item.title}
                      removeLabel={body.removeLabel}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <ProductPrice
                productCategory={
                  packItems &&
                  packItems[0].categories &&
                  packItems[0].categories[0]
                }
                price={{
                  text: roundedTotalPricePackItemsWithDiscount,
                }}
                compareAtPrice={{
                  text: roundedTotalPricePackItemsNoDiscount,
                }}
                currency={{ text: currency }}
                size='xl'
              />
              <div
                className='cursor-pointer mt-2 hover:underline flex justify-end'
                onClick={handleLineItemRemoveOnClick}>
                <Label
                  {...body.removeLabel}
                  size='sm'
                  icon={{
                    iconKey: 'IoMdTrash',
                    color: { classname: 'gray-400' },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    };
  });

  return (
    <div
      className={`px-6 bg-white flex no-scrollbar flex-col w-full ${
        isEmptyCart
          ? 'justify-center border-t border-gray-200'
          : 'justify-between'
      }`}>
      {/* Line items  */}
      <div
        id='cart-line-items'
        className=''>
        {loading[0] ? (
          <List
            divide
            listItems={[
              {
                spacing: 'py-4',
                children: <LoadingCartItem />,
              },
            ]}
          />
        ) : (
          <List
            divide
            listItems={[...packLineOrderList, ...lineOderItemList]}
          />
        )}
        {isEmptyCart && !loading[0] && (
          <EmptyCart {...formatContentfulComponentProps(body.emptyCart)} />
        )}
      </div>
    </div>
  );
};
